import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A composer draft that survives a page refresh.
 *
 * The composer window already survives navigation (it is mounted by `ComposerHost`, above
 * the router), but a reload tears down the whole JS context — and "I refreshed and lost the
 * letter I was three paragraphs into" is the same complaint as losing it to a nav.
 *
 * `AsyncStorage` rather than the secure store: it is localStorage on web and plain
 * AsyncStorage on native, and a draft is correspondence the sender is actively looking at,
 * not a credential. `secureStoreService` is for secrets and would put this in the Keychain.
 *
 * Writes are debounced — this is on the keystroke path of a multi-paragraph letter.
 */

/** Everything this feature writes lives under one prefix, so it can all be swept in one pass. */
const COMPOSER_KEY_PREFIX = 'composer-';
export const DRAFT_PREFIX = `${COMPOSER_KEY_PREFIX}draft:`;
const SAVE_DEBOUNCE_MS = 400;

/**
 * Every key carries the OWNER's user id: `composer-draft:<userId>:<kind>:<recordId>`.
 *
 * Storage outlives a session — `authService.signOut()` deletes three named keys and nothing
 * else — so on a shared browser an unscoped key let the next person to sign in restore, read
 * and send the previous user's unsent correspondence. Scoping is what makes that impossible;
 * `purgeForeignComposerDrafts` then clears what is not theirs. See `ComposerProvider`.
 */
export const draftStorageKey = (userId: string, key: string): string =>
	`${DRAFT_PREFIX}${userId}:${key}`;

/**
 * Delete every composer key that does not belong to `userId`.
 *
 * Called whenever the signed-in identity changes. Sign-out does not clear storage, so without
 * this the previous user's drafts sit in localStorage indefinitely — scoping stops the next
 * person READING them, and this stops them lingering at all. Also cleans up the unscoped keys
 * written before drafts were namespaced, whose owner segment can never match a real id.
 */
export async function purgeForeignComposerDrafts(userId: string | null, openPrefix: string): Promise<void> {
	/**
	 * `''` for anything under `composer-` with no owner segment — which is precisely the
	 * unscoped `composer-open` and `composer-draft:<kind>:<id>` keys written before drafts
	 * were namespaced. An empty string matches no real user id, so those always get swept up.
	 */
	const ownerOf = (key: string): string => {
		if (key.startsWith(DRAFT_PREFIX)) return key.slice(DRAFT_PREFIX.length).split(':')[0] ?? '';
		if (key.startsWith(openPrefix)) return key.slice(openPrefix.length).split(':')[0] ?? '';
		return '';
	};
	try {
		const keys = await AsyncStorage.getAllKeys();
		const stale = keys.filter((k) => k.startsWith(COMPOSER_KEY_PREFIX) && ownerOf(k) !== userId);
		if (stale.length) await AsyncStorage.multiRemove(stale);
	} catch {
		// Best-effort hygiene. Never block sign-in on it.
	}
}

export interface ComposerDraft<T> {
	/** The stored draft. Meaningless until `ready`. */
	restored: T | null;
	/** True once storage has been consulted, so `restored === null` is a real answer. */
	ready: boolean;
	save: (value: T) => void;
	clear: () => void;
}

/**
 * `key` is the draft's identity, e.g. `reply:<messageId>`; `scope` is the owning user id.
 * Either being empty disables persistence entirely.
 *
 * The scope is a PARAMETER rather than something this hook reads from the session, and that
 * is deliberate. Reaching for `useSession` here would drag `components/auth` — and through it
 * `expo-auth-session` — into every spec that renders a composer, which is a parse error in
 * the frontend test environment. `ComposerHost` is the only thing that needs to know who is
 * signed in; a composer rendered outside it simply does not persist, which is exactly right.
 */
export function useComposerDraft<T extends object>(
	scope: string | null | undefined,
	key: string | null,
): ComposerDraft<T> {
	const scopedKey = key && scope ? draftStorageKey(scope, key) : null;
	const [restored, setRestored] = useState<T | null>(null);
	const [ready, setReady] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	/**
	 * Guards a real race: the debounced write of the OLD draft can land after the key has
	 * changed, writing message A's letter under message B's key. Every queued write captures
	 * the key it was made for and is dropped if the key has moved on.
	 */
	const activeKey = useRef<string | null>(scopedKey);

	useEffect(() => {
		activeKey.current = scopedKey;
		setReady(false);
		setRestored(null);
		if (!scopedKey) { setReady(true); return; }
		let cancelled = false;
		void (async () => {
			try {
				const raw = await AsyncStorage.getItem(scopedKey);
				if (cancelled || activeKey.current !== scopedKey) return;
				setRestored(raw ? (JSON.parse(raw) as T) : null);
			} catch {
				// A corrupt or unreadable draft must never stop the composer opening.
				if (!cancelled) setRestored(null);
			} finally {
				if (!cancelled) setReady(true);
			}
		})();
		return () => { cancelled = true; };
	}, [scopedKey]);

	const save = useCallback((value: T) => {
		if (!scopedKey) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			if (activeKey.current !== scopedKey) return;
			void AsyncStorage.setItem(scopedKey, JSON.stringify(value)).catch(() => {});
		}, SAVE_DEBOUNCE_MS);
	}, [scopedKey]);

	const clear = useCallback(() => {
		if (timer.current) clearTimeout(timer.current);
		if (!scopedKey) return;
		void AsyncStorage.removeItem(scopedKey).catch(() => {});
	}, [scopedKey]);

	// Flush nothing on unmount — the pending timer is cleared, and whatever was last written
	// stands. Losing the final 400ms of typing is the correct trade against writing a draft
	// for a composer the user just closed.
	useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

	return { restored, ready, save, clear };
}
